import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import SeoSocialTags from '../components/SeoSocialTags';
import './TrustCentre.css';

/* Literal production URL for canonical; avoids CodeQL flags for env-derived href */
const CANONICAL_TRUST_URL = 'https://circumvent.io/trust';
const TRUST_PAGE_TITLE = 'Trust Center, SOC 2 | Circumvent';
const TRUST_PAGE_DESCRIPTION =
  'SOC 2 Type 1, security practices, and data residency for US and Australia. How Circumvent handles Cloud and AppSec findings data.';

const GOVERNANCE_TILES = [
  {
    icon: 'balance-scale',
    title: 'Integrity',
    description:
      'Background Checks, formal Code of Conduct and Acceptable Use Policies signed by all upon hire.',
  },
  {
    icon: 'graduation-cap',
    title: 'Competence',
    description:
      'Tailored security awareness, regular performance reviews and dedicated professional development in cloud security skills.',
  },
  {
    icon: 'route',
    title: 'Risk-Based Philosophy',
    description:
      'Management pursues objectives through a purpose-driven, risk-based approach, ensuring security is integrated into our business strategy, not just an afterthought.',
  },
];

const PLATFORM_SECURITY_ARCHITECTURE = [
  {
    icon: 'user-lock',
    title: 'Logical Access Control',
    body:
      'Strict "Least Privilege" and "Need to Know" implementation via SSO. Multi-factor authentication is mandatory for all production access.',
    bullets: ['Regular User Access Reviews', 'Individual Account Assignments'],
  },
  {
    icon: 'server',
    title: 'System Operations',
    body:
      'Comprehensive monitoring and alerting. Infrastructure is configured for auto-scaling and high availability across multiple AWS Availability Zones.',
    bullets: ['Daily Database Backups', 'Annual Restoration Testing'],
  },
  {
    icon: 'code-branch',
    title: 'Change Management',
    body:
      'Development, Testing, and Production environments are logically isolated. CI/CD pipelines enforce automated testing and peer review.',
    bullets: ['GitHub Actions Enforcement', 'Roll-back Version Control'],
  },
  {
    icon: 'network-wired',
    title: 'Physical & Network Security',
    body:
      'AWS handles core physical security. Circumvent maintains perimeter safety via firewalls (WAF), isolated VPCs, and automated threat detection and alerting.',
    bullets: ['Network Segregation', 'Encrypted Data-In-Transit'],
  },
  {
    icon: 'database',
    title: 'Data Governance',
    body:
      'Formal data classification and retention policies. Customer data is segmented and never used for AI training.',
    bullets: ['AES-256 Storage Encryption', 'Defined Disposal Controls'],
  },
  {
    icon: 'bell',
    title: 'Incident Management',
    body:
      'Documented response plans and dedicated emergency response teams ensure rapid triage, communication, and remediation of any adverse events.',
    bullets: ['Root Cause Analysis (RCA)', 'Regular Continuity Testing'],
  },
];

const TrustCentre = () => {
  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      document.querySelectorAll('.trust-reveal').forEach((el) => {
        el.classList.add('trust-reveal--visible');
      });
      return undefined;
    }

    const elements = document.querySelectorAll('.trust-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('trust-reveal--visible');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>{TRUST_PAGE_TITLE}</title>
        <meta name="description" content={TRUST_PAGE_DESCRIPTION} />
        <link rel="canonical" href={CANONICAL_TRUST_URL} />
      </Helmet>
      <SeoSocialTags
        title={TRUST_PAGE_TITLE}
        description={TRUST_PAGE_DESCRIPTION}
        url={CANONICAL_TRUST_URL}
      />

      <main className="page page--light">
        <section
          className="page-hero page-hero--dark"
          aria-labelledby="trust-hero-heading"
        >
          <div className="page-hero__inner">
            <p className="page-hero__eyebrow">Trust Center</p>
            <h1 id="trust-hero-heading" className="page-hero__title">
              SOC 2 Compliant
            </h1>
            <p className="page-hero__lead">
              Circumvent provides the world's first autonomous triage and remediation platform. Our Trust Center gives full visibility into the rigorous controls that safeguard your enterprise data.
            </p>
            <div className="page-hero__highlights-panel">
              <ul className="page-hero__highlights">
                <li>High Resilience</li>
                <li>Data Sovereignty</li>
                <li>Independent Auditor Verified</li>
              </ul>
            </div>
          </div>
        </section>

        <section
          className="page-section page-section--light trust-governance"
          aria-labelledby="trust-governance-heading"
        >
          <div className="page-container">
            <div className="page-section__header">
              <p className="page-section__eyebrow">Organizational governance</p>
              <h2 id="trust-governance-heading">Company-wide Security Culture</h2>
              <p>
                Our security posture starts at the executive level. We have established dedicated Security and Risk & Compliance teams that operate independently to ensure continuous oversight of our internal controls and ethical standards.
              </p>
            </div>
            <div className="trust-governance-panel">
              <div className="trust-governance-rows">
                {GOVERNANCE_TILES.map((tile, index) => (
                  <div
                    className="trust-principle-row trust-reveal"
                    key={tile.title}
                    style={{ transitionDelay: `${index * 0.07}s` }}
                  >
                    <div className="trust-principle-icon" aria-hidden="true">
                      <i className={`fa-solid fa-${tile.icon}`} />
                    </div>
                    <div className="trust-principle-copy">
                      <h3 className="trust-principle-title">{tile.title}</h3>
                      <p className="trust-principle-text">{tile.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          className="page-section trust-platform-architecture"
          aria-labelledby="trust-platform-architecture-heading"
        >
          <div className="page-container">
            <div className="page-section__header">
              <p className="page-section__eyebrow">Deep Technical Controls</p>
              <h2 id="trust-platform-architecture-heading">
                Platform Security Architecture
              </h2>
            </div>
            <div className="trust-tiles-grid">
              {PLATFORM_SECURITY_ARCHITECTURE.map((block, index) => (
                <div
                  className="trust-tile trust-tile--architecture trust-reveal"
                  key={block.title}
                  style={{ transitionDelay: `${index * 0.05}s` }}
                >
                  <div className="trust-tile-icon" aria-hidden="true">
                    <i className={`fa-solid fa-${block.icon}`} />
                  </div>
                  <h3 className="trust-tile-title">{block.title}</h3>
                  <p className="trust-tile-description">{block.body}</p>
                  <ul className="trust-tile-bullets">
                    {block.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div
              className="trust-data-privacy trust-reveal"
              aria-labelledby="trust-data-privacy-heading"
            >
              <h3 id="trust-data-privacy-heading">Data Privacy</h3>
              <p className="trust-compliance-intro">
                Circumvent respects your privacy and is committed to protecting your personal information. Our Privacy Policy describes how we collect, use, disclose, and safeguard information when you visit our website, create an account, or use our service.
              </p>
              <Link to="/privacy" className="trust-inline-link">
                Privacy Policy
                <span className="trust-inline-link-arrow" aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>

        <section
          className="page-section page-section--white trust-compliance-verification"
          aria-labelledby="trust-compliance-verification-heading"
        >
          <div className="page-container">
            <div className="page-section__header">
              <h2 id="trust-compliance-verification-heading">
                Circumvent Compliance Verification
              </h2>
              <p>
                Our internal controls are independently audited to ensure they meet the rigorous Trust Services Criteria for Security, Availability, and Confidentiality.
              </p>
            </div>
            <ul className="trust-soc-cards trust-reveal">
              <li>
                <div className="trust-soc-card">
                  <div className="trust-soc-card-top">
                    <strong>SOC 2 Type 1</strong>
                    <span className="trust-badge trust-badge--active">Active</span>
                  </div>
                  <p className="trust-soc-card-detail">
                    Attestation report date: March 31, 2026.
                    <br />
                    Auditor: Sensiba LLP.
                  </p>
                </div>
              </li>
              <li>
                <div className="trust-soc-card">
                  <div className="trust-soc-card-top">
                    <strong>SOC 2 Type 2</strong>
                    <span className="trust-badge trust-badge--roadmap">Roadmap</span>
                  </div>
                  <p className="trust-soc-card-detail">Preparation in progress.</p>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section
          className="page-section page-section--light trust-resource-vault"
          aria-labelledby="trust-resource-vault-heading"
        >
          <div className="page-container">
            <div className="page-section__header">
              <h2 id="trust-resource-vault-heading" className="trust-vault-title">
                The Resource Vault
              </h2>
              <p className="trust-vault-lead">
                Access our confidential documentation and independent audit reports.
              </p>
            </div>
            <div className="trust-governance-panel trust-reveal">
              <div className="trust-panel-body">
                <div className="trust-doc-row">
                  <div className="trust-doc-meta">
                    <span className="trust-doc-label">SOC 2 Type 1 Audit Report</span>
                    <span className="trust-doc-date">Issued: April 8, 2026</span>
                    <p className="trust-doc-nda-fine-print">
                      Mutual NDA Required for full reports
                    </p>
                  </div>
                  <Link to="/#contact" className="btn btn-gradient trust-doc-request-btn">
                    Request Access
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default TrustCentre;
